<template>
    <div class="row wrapper">
        <div class="col-xl-2 col-lg-12">
            <div class="markets-amount">{{ forecastWindows.length }} Markets</div>
            <div class="legend">
                <div class="roundedNumber" :style="{ backgroundColor: FORECAST_WINDOW_COLORS.VALIDATED }">
                    {{
                        forecastWindows.reduce((prev, curr) => {
                            return curr.isValidated ? prev + 1 : prev;
                        }, 0)
                    }}
                </div>
                <div>validated</div>
            </div>
            <div class="legend">
                <div class="roundedNumber" :style="{ backgroundColor: FORECAST_WINDOW_COLORS.SUBMITTED }">
                    {{ forecastWindows.filter(fw => fw.status === FORECAST_WINDOW_STATUS.SUBMITTED).length }}
                </div>
                {{ statusNameWindow(FORECAST_WINDOW_STATUS.SUBMITTED) }}
            </div>
            <div class="legend">
                <div class="roundedNumber" :style="{ backgroundColor: FORECAST_WINDOW_COLORS.RE_CHALLENGED }">
                    {{ forecastWindows.filter(fw => fw.status === FORECAST_WINDOW_STATUS.RE_CHALLENGED).length }}
                </div>
                {{ statusNameWindow(FORECAST_WINDOW_STATUS.RE_CHALLENGED) }}
            </div>
        </div>
        <div class="col-xl col-lg-12">
            <div class="row">
                <div class="col-xl-auto col-lg-12 px-4 zone" v-for="forecastWindow in forecastWindows" :key="forecastWindow.id">
                    <div style="display: flex; border-bottom: 1px dotted black; padding-bottom: 1px;width: 100%;">
                        <DefaultButton
                            v-b-tooltip.hover.right="statusName(forecastWindow.forecast.status).toUpperCase()"
                            :disabled="!forecastWindowAccessible"
                            :size="'xs'"
                            :style="{ backgroundColor: statusColor(forecastWindow.forecast.status), color: '#000000', fontWeight: 'bold' }"
                            @click="$router.push(`${ROUTES.BASE}/window/${forecastWindow.id}`)"
                        >
                            <span>View</span>
                            <span class="statusBadge">{{ statusLetter(forecastWindow.forecast.status) }}</span>
                        </DefaultButton>
                        <div style="min-width: 180px" class="d-flex align-items-center">
                            <p class="multiValidation my-0 d-inline-block" :class="{ hidden: !forecastWindow.multiValidation }"><MarketIcon /></p>
                            <p class="m-0 mx-2 d-inline-block">
                                <span v-if="forecastWindow.zone.name.length < 16">{{ forecastWindow.zone.name }}</span>
                                <span v-else v-b-tooltip.hover.bottom="forecastWindow.zone.name">{{ formatZoneName(forecastWindow.zone.name) }}</span>
                            </p>
                        </div>
                        <div style="width: 44px">
                            <div
                                class="roundedNumber"
                                v-if="forecastWindow.status === FORECAST_WINDOW_STATUS.SUBMITTED"
                                :style="{ backgroundColor: FORECAST_WINDOW_COLORS.SUBMITTED }"
                            >
                                <SubmittedIcon class="icon" />
                            </div>
                            <div v-if="forecastWindow.isValidated" class="roundedNumber"><CheckMarkIcon class="icon" /></div>
                            <div
                                v-if="forecastWindowsIsRechallenged(forecastWindow)"
                                class="roundedNumber"
                                :style="{ backgroundColor: FORECAST_WINDOW_COLORS.RE_CHALLENGED }"
                            >
                                <ReChallengeIcon class="icon" />
                            </div>
                        </div>
                        <div style=" display: flex;flex-grow: 1;align-items:center;text-align: right;width:60px;min-width: 60px">
                            <p v-if="isDisabledZone(forecastWindow.zone.id)" class="text-right w-100 m-0 small">
                                <i style="font-size: 12px !important;">(DELETED)</i>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { FORECAST_WINDOW_COLORS, getStatusColor, getStatusLetter, getStatusName, getStatusNameWindow } from "@/components/forecasts/utils/utilis";
import MarketIcon from "@/components/forecasts/elements/icons/MarketIcon";
import CheckMarkIcon from "@/components/forecasts/elements/icons/CheckMarkIcon";
import ZoneStatus from "@/components/forecasts/elements/ZoneStatus";
import { FORECAST_STATUS, FORECAST_WINDOW_STATUS } from "@/components/forecasts/constants";
import Diffusion from "@/components/modal/Diffusion";
import { ROUTES } from "@/components/forecasts/router";
import Spinner from "@/components/common/Spinner";
import RoundedButton from "@/catalog/components/rounded-button";
import DefaultButton from "@/components/forecasts/elements/form/DefaultButton";
import ReChallengeIcon from "@/components/forecasts/elements/icons/ReChallengeIcon";
import SubmittedIcon from "@/components/forecasts/elements/icons/SubmittedIcon";
import { PROFILES } from "@/plugins/AccessUserPlugin";
import { moment } from "@/utils/moment";
import { mapActions, mapGetters } from "vuex";
import TryLoad from "@/mixins/TryLoadMixin";

export default {
    components: { DefaultButton, RoundedButton, Spinner, Diffusion, MarketIcon, CheckMarkIcon, ZoneStatus, ReChallengeIcon, SubmittedIcon },
    props: {
        forecast: {
            type: Object,
            required: true
        },
        forecastWindows: {
            type: Array,
            require: true
        }
    },
    mixins: [TryLoad],
    data() {
        return {
            // brandZones: [],
            FORECAST_WINDOW_STATUS,
            FORECAST_WINDOW_COLORS,
            ROUTES,
            NUMBER_OF_ZONE_COLUMNS: 4,
            currentForecastWindowIndex: 0
        };
    },
    async created() {
        await this.tryLoad(() => this.populateAdminBrandZones({ reload: true }));
    },
    computed: {
        ...mapGetters("forecast", { brandZones: "getAdminBrandZones" }),
        forecastWindowsInColumns() {
            let currentColumn = 0;
            let columns = [];
            this.forecastWindows.forEach(fcw => {
                columns[currentColumn] = columns[currentColumn] || [];
                columns[currentColumn].push(fcw);
                if (currentColumn === this.NUMBER_OF_ZONE_COLUMNS - 1) {
                    currentColumn = 0;
                } else {
                    currentColumn++;
                }
            });
            return columns;
        },
        forecastWindowAccessible() {
            return this.hasAccessByProfile(PROFILES.FORECAST_MANAGER) || this.forecast.status !== FORECAST_STATUS.DRAFT;
        }
    },
    methods: {
        ...mapActions("forecast", { populateAdminBrandZones: "populateAdminBrandZones" }),
        statusNameWindow(status) {
            return getStatusNameWindow(status.deleted ? "DELETE" : status);
        },
        statusName(status) {
            return getStatusName(this.forecast.deleted ? "DELETE" : status);
        },
        statusColor(status) {
            return getStatusColor(this.forecast.deleted ? "DELETE" : status);
        },
        statusLetter(status) {
            return getStatusLetter(this.forecast.deleted ? "DELETE" : status);
        },
        forecastWindowsIsRechallenged(fw) {
            const endDate = moment(fw.endDate);
            const today = moment();
            return fw.status === FORECAST_WINDOW_STATUS.RE_CHALLENGED && endDate.format("YYYYMMDD") >= today.format("YYYYMMDD");
        },
        formatZoneName(zoneName) {
            if (!zoneName) {
                return "";
            }
            return zoneName.substr(0, 15) + "...";
        },
        isDisabledZone(zoneId) {
            const brandZone = this.brandZones.find(bz => bz.zone.id === zoneId);
            if (!brandZone) {
                return true;
            }
            return !brandZone.active;
        }
    }
};
</script>
<style scoped lang="scss">
@import "../../css/forecasts";
.wrapper {
    text-transform: uppercase;
}
.markets-amount {
    font-weight: bold;
    font-size: 1.3em;
}
.legend {
    display: flex;
    padding: 5px 0;
    .roundedNumber {
        margin-right: 3px;
    }
}
.roundedNumber {
    height: 18px;
    width: 18px;
    border-radius: 50%;
    display: inline-block;
    text-align: center;
    font-weight: bold;
    color: #ffffff;
}
.zone {
    display: flex;
    padding: 3px 0;
}
.hidden {
    visibility: hidden;
}
.cutWord {
    width: 90px;
    margin-left: 3px;
}
.icon {
    width: 18px;
    height: 18px;
    vertical-align: middle;
}
.multiValidation {
    margin-left: 2px;
    height: 20px;
    width: 20px;
}
.statusBadge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #000000;
    color: #ffffff;
    border-radius: 12px;
    height: 20px;
    min-width: 20px;
}
</style>



// WEBPACK FOOTER //
// ZonesDetail.vue?620bc191